/**
 * Store Pinia des unités de stock (composition API)
 */
import { defineStore } from "pinia";
import { ref } from "vue";
import StockUnityService from "../services/StockUnityService";
import { IStockUnity } from "../models/stockUnity";
import { IStockUnityForm } from "../models/forms/stockUnityForm";
import { IStockUnityApiResponse } from "../models/api/stockUnityApiResponse";
import { IStockUnityFilter } from "../models/filters/stockUnityFilter";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { ICenter } from "../models/center";
import { IArticle } from "../models/article";
import { IParameter } from "../models/parameter";
import { IManufactoringProduction } from "../models/manufactoringProduction";
import { IProduction } from "../models/production";
import { IBoar } from "../models/boar";
import { useArticleStore } from "./article.store";
import { useParameterStore } from "./parameter.store";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";

export const useStockUnityStore = defineStore("stockunites", () => {
    // State
    const datas = ref<IStockUnity[]>([]);
    const current = ref<IStockUnity>({} as IStockUnity);
    const itemForm = ref<IStockUnityForm>({} as IStockUnityForm);
    const isLoading = ref<boolean>(false);

    // Actions - Hydration
    /**
     * Hydrate une liste d'unités de stock en batch (optimisé pour la performance)
     * - Charge articleStore et parameterStore si nécessaires (en parallèle)
     * - Crée des Maps d'index pour des lookups O(1)
     * - manufactoringProduction, production, boar laissés undefined (chargés via get() à la demande)
     */
    async function hydrateStockUnities(items: IStockUnityApiResponse[]): Promise<IStockUnity[]> {
        if (items.length === 0) return [];

        // 1. Récupérer les stores dépendants
        const articleStore = useArticleStore();
        const parameterStore = useParameterStore();

        // 2. Charger les stores dépendants en parallèle si nécessaire
        const loadPromises: Promise<unknown>[] = [];

        if (articleStore.datas.length === 0) {
            loadPromises.push(articleStore.load({ page: 1, pageSize: 0 }));
        }
        if (parameterStore.datas.length === 0) {
            loadPromises.push(parameterStore.load({ page: 1, pageSize: 0 }));
        }

        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Créer des Maps pour des lookups O(1)
        const articleMap = new Map(articleStore.datas.map(a => [a.id, a]));
        const parameterMap = new Map(parameterStore.datas.map(p => [p.id, p]));

        // 4. Hydrater toutes les unités en une seule passe
        const models: IStockUnity[] = [];
        for (const item of items) {
            const model = { ...item } as unknown as IStockUnity;

            // Lier les références (lookup O(1) via Map)
            model.article = articleMap.get(item.articleId)!;
            if (item.conditionnementId) model.conditionnement = parameterMap.get(item.conditionnementId);
            if (item.aptitudeConservationId) model.aptitudeConservation = parameterMap.get(item.aptitudeConservationId);
            if (item.geneticQualityId) model.geneticQuality = parameterMap.get(item.geneticQualityId);
            if (item.NNId) model.NN = parameterMap.get(item.NNId);
            // manufactoringProduction, production, boar → chargés à la demande via get()

            models.push(model);
        }

        return models;
    }

    // Actions

    /**
     * Chargement des données dans le store
     */
    async function load(filter: IStockUnityFilter) {
        isLoading.value = true;
        const result = await StockUnityService.getAll<IStockUnityFilter, IStockUnityApiResponse>(filter);
        isLoading.value = false;
        datas.value = [];

        if (!result.error) {
            datas.value = await hydrateStockUnities(result.data as IStockUnityApiResponse[]);
        }

        return result;
    }

    /**
     * Récupération d'une unité de stock par son identifiant
     */
    async function get(id: number) {
        const result = await StockUnityService.getById<IStockUnityApiResponse>(id);

        if (!result.error) {
            const [hydratedItem] = await hydrateStockUnities([result.data as IStockUnityApiResponse]);

            const index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = hydratedItem;

            current.value = hydratedItem;
            itemForm.value = toForm(hydratedItem);
        }

        return result;
    }

    /**
     * Récupération de l'objet dans le store
     */
    async function searchStoreById(id: number): Promise<IStockUnity> {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value as IStockUnity);
        return current.value;
    }

    /**
     * Mise à jour ou création d'un objet
     */
    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<IStockUnity | IDataOperationResult<IStockUnity>>;

        if (itemForm.value.id > 0) {
            result = await StockUnityService.update(itemForm.value);

            if (!result.error) {
                const [hydratedItem] = await hydrateStockUnities([result.data as unknown as IStockUnityApiResponse]);
                current.value = hydratedItem;

                if (maj) {
                    const index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = "Item non trouvé dans le store";
                        return new ServiceOperationResult<IDataOperationResult<IStockUnity>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await StockUnityService.create(itemForm.value);

            if (!result.error) {
                const [hydratedItem] = await hydrateStockUnities([result.data as unknown as IStockUnityApiResponse]);
                current.value = hydratedItem;
                if (datas.value.findIndex((a) => a.id == current.value?.id) == -1) {
                    datas.value.push(current.value);
                }
            }
        }

        return result;
    }

    /**
     * Delete de l'objet
     */
    async function deleteAction() {
        const result = await StockUnityService.delete<IStockUnityApiResponse>(current.value.id);

        if (!result.error) {
            const index = datas.value.findIndex((a) => a.id == current.value.id);
            if (index >= 0) {
                datas.value.splice(index, 1);
            }

            current.value = newItem();
            itemForm.value = newForm();
        }

        return result;
    }

    /**
     * Transformation de l'objet en formulaire
     */
    function toForm(item: IStockUnity): IStockUnityForm {
        const form = {} as IStockUnityForm;
        form.id = item.id;
        form.articleId = item.article.id;
        form.conditionnementId = item.conditionnement ? item.conditionnement.id : 0;
        form.datePeremption = item.datePeremption;
        form.heterospermie = item.heterospermie;
        form.lotNumber = item.lotNumber;
        form.manufactoringProductionId = item.manufactoringProduction ? item.manufactoringProduction.id : 0;
        form.productionId = item.production ? item.production.id : 0;
        form.boarId = item.boar ? item.boar.id : 0;
        form.spzDose = item.spzDose;
        form.volumeDose = item.volumeDose;
        itemForm.value = form;
        return form;
    }

    /**
     * Permet d'initialiser un objet vide pour le formulaire
     */
    function newForm(lienId: number = 0): IStockUnityForm {
        itemForm.value = {
            id: 0,
            articleId: 0,
            conditionnementId: 0,
            datePeremption: new Date(),
            heterospermie: 0,
            lotNumber: "",
            manufactoringProductionId: 0,
            productionId: 0,
            spzDose: 0,
            volumeDose: 0,
            boarId: 0
        } as IStockUnityForm;
        return itemForm.value;
    }

    /**
     * Permet d'initialiser un objet vide pour les referentiels
     */
    function newItem(lien?: ICenter): IStockUnity {
        current.value = {
            id: 0,
            article: { id: 0 } as IArticle,
            conditionnement: { id: 0 } as IParameter,
            manufactoringProduction: { id: 0 } as IManufactoringProduction,
            production: { id: 0 } as IProduction,
            datePeremption: new Date(),
            heterospermie: 0,
            lotNumber: "",
            spzDose: 0,
            volumeDose: 0,
            boar: { id: 0 } as IBoar
        } as IStockUnity;
        return current.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,
        isLoading,
        // Actions
        hydrateStockUnities,
        load,
        get,
        searchStoreById,
        save,
        delete: deleteAction,
        toForm,
        newForm,
        newItem,
    };
});

signalRManager.connection.on("MajStockUnity", async (payload: IPayloadStore) => {
    const store = useStockUnityStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateStockUnities([payload.data as IStockUnityApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateStockUnities([payload.data as IStockUnityApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
